import { useForm } from '@tanstack/react-form'
import { cuentaSchema, DEFAULT_CUENTA_VALUES, type CuentaFormValues } from './cuentaSchema'

export interface UseCuentaFormOptions {
  defaultValues?: Partial<CuentaFormValues>
  onSubmit?: (data: CuentaFormValues) => Promise<void> | void
}

/**
 * Hook para manejar el formulario de Cuenta con TanStack Form + Zod
 *
 * @example
 * const { form } = useCuentaForm({
 *   defaultValues: cuenta ? mapCuentaToForm(cuenta) : undefined,
 *   onSubmit: async (data) => {
 *     await crearCuenta(data)
 *   }
 * })
 */
export function useCuentaForm(options: UseCuentaFormOptions = {}) {
  const { defaultValues, onSubmit } = options

  const form = useForm({
    defaultValues: {
      ...DEFAULT_CUENTA_VALUES,
      ...defaultValues,
    } as CuentaFormValues,
    validators: {
      onSubmit: ({ value }) => {
        const result = cuentaSchema.safeParse(value)
        if (!result.success) {
          return result.error.issues.map((issue) => ({
            message: issue.message,
            path: issue.path,
          }))
        }
        return undefined
      },
    },
    onSubmit: async ({ value }) => {
      if (onSubmit) {
        await onSubmit(value as CuentaFormValues)
      }
    },
  })

  return { form }
}

/**
 * Helper para extraer mensaje de error de TanStack Form
 * Compatible con Zod v4 (StandardSchemaV1Issue)
 */
export function getFieldError(errors: unknown[]): string | undefined {
  const first = errors[0]
  if (!first) return undefined
  if (typeof first === 'string') return first
  if (typeof first === 'object' && first !== null && 'message' in first) {
    return (first as { message: string }).message
  }
  return undefined
}

// Tipos exportados para uso en componentes
export type UseCuentaFormReturn = ReturnType<typeof useCuentaForm>
export type CuentaForm = UseCuentaFormReturn['form']
